import { useEffect, useMemo, useState } from "react";

/**
 * Milliseconds left until `expiresAt`, ticking every second; `null` when there is no expiry to count down to.
 *
 * The clock is the state and the remainder is derived from it, rather than storing the remainder itself: a
 * new `expiresAt` then takes effect on the same render, with no effect writing state to catch up.
 */
export function usePayinCountdown(expiresAt: string | null | undefined): number | null {
  const deadline = useMemo(() => (expiresAt ? new Date(expiresAt).getTime() : null), [expiresAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline === null) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return deadline === null ? null : deadline - now;
}

/** Formats as mm:ss, or h:mm:ss past an hour. Clamped at zero — never a negative clock. */
export function formatPayinRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const seconds = String(total % 60).padStart(2, "0");
  const minutes =
    total >= 3600 ? String(Math.floor(total / 60) % 60).padStart(2, "0") : String(Math.floor(total / 60));
  const hours = Math.floor(total / 3600);
  return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}
